import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';

import { formatQuantity } from '~components/Project/QuotePdf/utils';
import { ApproveChargesSummary } from '~hooks/useApproveCharges/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';

import { NO_VALUE_PLACEHOLDER } from '../chargesGrid';

interface ChargesSummaryTilesProps {
  isError: boolean;
  isLoading: boolean;
  summary: ApproveChargesSummary | null;
}

export default function ChargesSummaryTiles({
  isError,
  isLoading,
  summary,
}: ChargesSummaryTilesProps) {
  const { currencyFormatter } = useCompanyCurrency();

  const tiles = [
    {
      key: 'receivable',
      label: t('approvals.approve_charges.summary.receivable'),
      value: summary && currencyFormatter(summary.receivableTotal),
    },
    {
      key: 'payable',
      label: t('approvals.approve_charges.summary.payable'),
      value: summary && currencyFormatter(summary.payableTotal),
    },
    {
      key: 'jobs',
      label: t('approvals.approve_charges.summary.jobs'),
      value: summary && String(summary.jobsCount),
    },
    {
      key: 'loads',
      label: t('approvals.approve_charges.summary.loads'),
      value: summary && String(summary.loadsCount),
    },
    {
      key: 'missing_tickets',
      label: t('approvals.approve_charges.summary.missing_tickets'),
      value: summary && String(summary.missingTicketsCount),
    },
    {
      key: 'tons',
      label: t('approvals.approve_charges.summary.tons'),
      value: summary && formatQuantity(summary.tons),
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))`,
      }}
    >
      {tiles.map((tile) => (
        <Box
          key={tile.key}
          data-test-id={`charges-summary-tile-${tile.key}`}
          sx={(theme) => ({
            bgcolor: theme.semantic.surfaceRaised,
            border: `solid 1px ${theme.brandV2.colors.treadGray6}`,
            borderRadius: theme.spacing(),
            px: 2,
            py: 1.25,
          })}
        >
          <Typography
            sx={(theme) => ({
              color: theme.brandV2.colors.treadGray4,
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            })}
            variant="caption"
          >
            {tile.label}
          </Typography>
          {isLoading ? (
            <Skeleton
              data-test-id={`charges-summary-tile-${tile.key}-loading`}
              width="60%"
            >
              <Typography sx={{ fontSize: '20px' }}>0</Typography>
            </Skeleton>
          ) : (
            <Typography sx={{ fontSize: '20px', fontWeight: 700 }}>
              {!isError && tile.value ? tile.value : NO_VALUE_PLACEHOLDER}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
